package com.eladfinish.bible.contest.screens.pyramidCrossword

import com.eladfinish.bible.contest.PyramidCrosswordGuide_txt
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.features.review.ReviewMoment
import com.eladfinish.bible.contest.features.review.ReviewPrompter
import com.eladfinish.bible.contest.platform.dispatchers.IoDispatcher
import com.eladfinish.bible.contest.pyramid_questions_json
import com.eladfinish.bible.contest.screens.pyramidCrossword.model.PyramidJsonRoot
import com.eladfinish.bible.contest.screens.pyramidCrossword.model.PyramidQuestion
import com.eladfinish.bible.contest.ui.components.dialogs.HelpContentType
import com.eladfinish.bible.contest.ui.menus.actions.PyramidCrosswordToolbarAction
import com.eladfinish.bible.contest.utils.wordsWithFinalLettersReplacedByRegularNoSpaces
import com.russhwolf.settings.Settings
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableMap
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.withContext
import kotlinx.serialization.json.Json

class PyramidCrosswordStore(
    private val settings: Settings,
    private val assetReader: AssetReader,
    private val reviewPrompter: ReviewPrompter,
//    private val getPyramidsUseCase: GetPyramidsUseCase, // TODO: use it once pyramids content are fetched from Google Spreadsheet
) : ViewModelStore<PyramidCrosswordState, PyramidCrosswordAction, PyramidCrosswordEffect>(
    tag = "PyramidCrosswordStore",
) {

    companion object {
        const val SKIP_READ_ME_PREF = "skipReadMePyramidCrossword"
    }

    private val isSkipReadMeChecked: Boolean
        get() = settings.getBoolean(key = SKIP_READ_ME_PREF, defaultValue = false)

    private var allPyramidCollections: List<List<PyramidQuestion>> = emptyList()
    private var collectionOrder: List<Int> = emptyList()
    private var currentPyramidIndex = 0

    // A pyramid filled in from the solutions dialog is not the user's own success.
    private var solutionsShownForCurrentPyramid = false

    private val jsonParser = Json { ignoreUnknownKeys = true }

    override val stateFlow = MutableStateFlow(
        PyramidCrosswordState(
            isLoading = true,
            isGameFinished = false,
            isSkipReadMeChecked = isSkipReadMeChecked,
            dialogState = if (isSkipReadMeChecked) {
                PyramidCrosswordState.DialogState.None
            } else {
                PyramidCrosswordState.DialogState.Help(
                    helpContentType = getHelpAsset(),
                    isDontShowAgainChecked = isSkipReadMeChecked,
                )
            },
        )
    )

    init {
        dispatch(PyramidCrosswordAction.LoadPyramids)
    }

    override fun handleAction(action: PyramidCrosswordAction): PyramidCrosswordState {
        return when (action) {
            is PyramidCrosswordAction.PyramidsLoaded ->
                handlePyramidsLoaded(action.collections)

            PyramidCrosswordAction.LoadPyramids -> handleLoadPyramids()
            PyramidCrosswordAction.NextPyramid -> handleNextPyramid()
            is PyramidCrosswordAction.OnCellInput -> handleCellInput(
                rowIndex = action.rowIndex,
                cellIndex = action.cellIndex,
                char = action.char
            )

            is PyramidCrosswordAction.OnCellBackspace -> handleCellBackspace(rowIndex = action.rowIndex, cellIndex = action.cellIndex)
            is PyramidCrosswordAction.OnCellFocused -> handleCellFocused(rowIndex = action.rowIndex, cellIndex = action.cellIndex)
            PyramidCrosswordAction.HideDialog -> handleHideDialog()
            PyramidCrosswordAction.OnPrevRow -> handlePrevRow()
            PyramidCrosswordAction.OnNextRow -> handleNextRow()
            is PyramidCrosswordAction.ToggleHelp -> handleToggleHelp(shouldShowNextTime = action.shouldShowNextTime)
            is PyramidCrosswordAction.ToolbarClicked -> handleToolbarClicked(toolbarAction = action.toolbarAction)
        }
    }

    private fun handleToolbarClicked(toolbarAction: PyramidCrosswordToolbarAction): PyramidCrosswordState {
        return when (toolbarAction) {
            PyramidCrosswordToolbarAction.Help -> handleHelpClicked()
            PyramidCrosswordToolbarAction.Solutions -> handleSolutionsClicked()
        }
    }

    private fun handleToggleHelp(shouldShowNextTime: Boolean): PyramidCrosswordState {
        settings.putBoolean(key = SKIP_READ_ME_PREF, value = shouldShowNextTime)
        return currentState.copy(isSkipReadMeChecked = shouldShowNextTime)
    }

    private fun handlePyramidsLoaded(
        collections: List<List<PyramidQuestion>>,
    ): PyramidCrosswordState {

        if (collections.isEmpty()) {
            return currentState.copy(
                isLoading = false,
            )
        }

        allPyramidCollections = collections
        collectionOrder = collections.indices.shuffled()
        currentPyramidIndex = 0

        return startNewPyramid(
            currentState.copy(isLoading = false),
        )
    }

    private fun handleLoadPyramids(): PyramidCrosswordState {
        launchEffect {
            withContext(IoDispatcher) {
                try {
                    val jsonText =
                        assetReader.readFile(SharedRes.assets.pyramid.pyramid_questions_json)

                    val root =
                        jsonParser.decodeFromString(PyramidJsonRoot.serializer(), jsonText)

                    val collections = root.playableCollections()

                    dispatch(
                        PyramidCrosswordAction.PyramidsLoaded(
                            collections = collections,
                        ),
                    )
                } catch (e: Exception) {
                    Napier.e("Failed to load pyramids", e)
                    dispatch(
                        PyramidCrosswordAction.PyramidsLoaded(
                            collections = emptyList(),
                        ),
                    )
                }
            }
        }

        return currentState.copy(
            isLoading = true,
        )
    }

    private fun handleNextPyramid(): PyramidCrosswordState {
        currentPyramidIndex++
        return if (currentPyramidIndex < collectionOrder.size) {
            onlyEffect(PyramidCrosswordEffect.PlayPageTurningSound)
            startNewPyramid(currentState)
        } else {
            onlyEffect(PyramidCrosswordEffect.ShowToast("אין עוד תשבצים"))
            currentState
        }
    }

    private fun startNewPyramid(state: PyramidCrosswordState): PyramidCrosswordState {
        solutionsShownForCurrentPyramid = false
        val collectionIndex = collectionOrder[currentPyramidIndex]
        val questions = allPyramidCollections[collectionIndex]

        return state.copy(
            currentQuestions = questions.toPersistentList(),
            userAnswers = questions.indices.associateWith { "" }.toImmutableMap(),
            rowValidation = questions.indices.associateWith { null as Boolean? }.toImmutableMap(),
            focusedRowIndex = 0,
            focusedCellIndex = 0,
            isLoading = false,
            isGameFinished = false,
        )
    }

    private fun handleCellInput(rowIndex: Int, cellIndex: Int, char: String): PyramidCrosswordState {
        val currentAnswer = currentState.userAnswers[rowIndex].orEmpty()
        val charArray =
            currentAnswer
                .padEnd(length = currentState.currentQuestions[rowIndex].numberOfLettersInAnswer, padChar = ' ')
                .toCharArray()

        if (cellIndex < charArray.size) {
            charArray[cellIndex] = char.getOrNull(0) ?: ' '
        }

        val newAnswer = charArray.concatToString().trimEnd()
        val newUserAnswers = currentState.userAnswers.toMutableMap().apply {
            put(rowIndex, newAnswer)
        }.toImmutableMap()

        var newState = currentState.copy(userAnswers = newUserAnswers)

        // Validate row if completed
        if (newAnswer.length == currentState.currentQuestions[rowIndex].numberOfLettersInAnswer) {
            newState = validateRow(newState, rowIndex)
        } else {
            val newRowValidation = newState.rowValidation.toMutableMap().apply {
                put(rowIndex, null)
            }.toImmutableMap()
            newState = newState.copy(rowValidation = newRowValidation)
        }

        // Auto focus logic
        val question = newState.currentQuestions[rowIndex]
        val isLastCellInRow = cellIndex == question.numberOfLettersInAnswer - 1

        val nextRow: Int
        val nextCell: Int

        if (isLastCellInRow) {
            if (rowIndex < newState.currentQuestions.size - 1) {
                nextRow = rowIndex + 1
                nextCell = 0
            } else {
                nextRow = 0
                nextCell = 0
            }
        } else {
            nextRow = rowIndex
            nextCell = cellIndex + 1
        }

        return newState.copy(
            focusedRowIndex = nextRow,
            focusedCellIndex = nextCell
        )
    }

    private fun handleCellBackspace(
        rowIndex: Int,
        cellIndex: Int,
    ): PyramidCrosswordState {

        val question = currentState.currentQuestions[rowIndex]
        val rowLength = question.numberOfLettersInAnswer

        val currentAnswer = currentState.userAnswers[rowIndex].orEmpty()
            .padEnd(rowLength, ' ')
            .toCharArray()

        val hasCharInCell =
            cellIndex < currentAnswer.size && currentAnswer[cellIndex] != ' '

        // --- CASE 1: Cell has text -> clear it, keep focus ---
        if (hasCharInCell) {
            currentAnswer[cellIndex] = ' '

            val newAnswer = currentAnswer.concatToString().trimEnd()
            val newUserAnswers = currentState.userAnswers.toMutableMap().apply {
                put(rowIndex, newAnswer)
            }.toImmutableMap()

            val newRowValidation = currentState.rowValidation.toMutableMap().apply {
                put(rowIndex, null)
            }.toImmutableMap()

            return currentState.copy(
                userAnswers = newUserAnswers,
                rowValidation = newRowValidation,
                focusedRowIndex = rowIndex,
                focusedCellIndex = cellIndex
            )
        }

        // --- CASE 2: Cell is empty -> move to previous cell ---
        val (prevRow, prevCell) = findPreviousCell(rowIndex, cellIndex)

        return currentState.copy(
            focusedRowIndex = prevRow,
            focusedCellIndex = prevCell
        )
    }

    private fun findPreviousCell(
        rowIndex: Int,
        cellIndex: Int,
    ): Pair<Int, Int> {

        // Same row, move left
        if (cellIndex > 0) {
            return rowIndex to (cellIndex - 1)
        }

        // First cell in row -> go to previous row
        val prevRow =
            if (rowIndex > 0) rowIndex - 1
            else currentState.currentQuestions.size - 1

        val prevRowLength =
            currentState.currentQuestions[prevRow].numberOfLettersInAnswer

        return prevRow to (prevRowLength - 1)
    }

    private fun handleCellFocused(rowIndex: Int, cellIndex: Int): PyramidCrosswordState {
        return currentState.copy(
            focusedRowIndex = rowIndex,
            focusedCellIndex = cellIndex
        )
    }

    private fun validateRow(state: PyramidCrosswordState, rowIndex: Int): PyramidCrosswordState {
        val question = state.currentQuestions[rowIndex]
        val userAnswer = state.userAnswers[rowIndex].orEmpty()
        val correctAnswer = question.answerNoSpaces.lowercase()

        // Also accept the regular (non-final) form of a word's last letter,
        // e.g. "שמנ" for "שמן".
        val noSofiot = question.answer.wordsWithFinalLettersReplacedByRegularNoSpaces().lowercase()

        val isCorrect = userAnswer.lowercase() == correctAnswer || userAnswer.lowercase() == noSofiot

        val newRowValidation = state.rowValidation.toMutableMap().apply {
            put(rowIndex, isCorrect)
        }.toImmutableMap()

        if (isCorrect) {
            onlyEffect(PyramidCrosswordEffect.VibrateShort)
            onlyEffect(PyramidCrosswordEffect.PlayCorrectSound)
        } else {
            onlyEffect(PyramidCrosswordEffect.VibrateLong)
            onlyEffect(PyramidCrosswordEffect.PlayIncorrectSound)
        }

        val allCorrect = newRowValidation.values.all { it == true }
        if (allCorrect && newRowValidation.size == state.currentQuestions.size) {
            onlyEffect(
                PyramidCrosswordEffect.GameCompleted(
                    toastMessage = "כל הכבוד!",
                ),
            )
            if (!solutionsShownForCurrentPyramid) {
                launchEffect { reviewPrompter.onSuccessMoment(moment = ReviewMoment.PYRAMID) }
            }
            return state.copy(
                rowValidation = newRowValidation,
                isGameFinished = true
            )
        }

        return state.copy(rowValidation = newRowValidation)
    }

    private fun handleHelpClicked(): PyramidCrosswordState {
        return currentState.copy(
            dialogState = PyramidCrosswordState.DialogState.Help(
                helpContentType = getHelpAsset(),
                isDontShowAgainChecked = currentState.isSkipReadMeChecked,
            )
        )
    }

    private fun getHelpAsset(): HelpContentType.Asset =
        HelpContentType.Asset(SharedRes.assets.other.PyramidCrosswordGuide_txt)

    private fun handleSolutionsClicked(): PyramidCrosswordState {
        val solutions = currentState.currentQuestions.withIndex().joinToString(separator = "\n") {
            "${it.index + 1}. ${it.value.question}: ${it.value.answer}"
        }
        solutionsShownForCurrentPyramid = true
        return currentState.copy(dialogState = PyramidCrosswordState.DialogState.Solutions(content = solutions))
    }

    private fun handleHideDialog(): PyramidCrosswordState {
        return currentState.copy(dialogState = PyramidCrosswordState.DialogState.None)
    }

    private fun handlePrevRow(): PyramidCrosswordState {
        val count = currentState.currentQuestions.size
        if (count == 0) return currentState
        return currentState.copy(
            focusedRowIndex = (currentState.focusedRowIndex - 1 + count) % count,
            focusedCellIndex = 0
        )
    }

    private fun handleNextRow(): PyramidCrosswordState {
        val count = currentState.currentQuestions.size
        if (count == 0) return currentState
        return currentState.copy(
            focusedRowIndex = (currentState.focusedRowIndex + 1) % count,
            focusedCellIndex = 0
        )
    }
}
